import { useMutation } from "@tanstack/react-query"
import type {
  UseMutationOptions,
  UseMutationResult,
} from "@tanstack/react-query"

import client from "~/backend/axiosClient"

import type {
  DeleteCertificateRequestMutationRequest,
  DeleteCertificateRequestMutationResponse,
} from "../types/DeleteCertificateRequest"

type DeleteCertificateRequestClient = typeof client<
  DeleteCertificateRequestMutationResponse,
  Error,
  DeleteCertificateRequestMutationRequest
>
type DeleteCertificateRequest = {
  data: DeleteCertificateRequestMutationResponse
  error: Error
  request: DeleteCertificateRequestMutationRequest
  pathParams: never
  queryParams: never
  headerParams: never
  response: Awaited<ReturnType<DeleteCertificateRequestClient>>
  client: {
    parameters: Partial<Parameters<DeleteCertificateRequestClient>[0]>
    return: Awaited<ReturnType<DeleteCertificateRequestClient>>
  }
}
/**
 * @summary Delete certificate request
 * @link /v1/certificate-request/delete-certificate-request
 */
export function useDeleteCertificateRequest(
  options: {
    mutation?: UseMutationOptions<
      DeleteCertificateRequest["response"],
      DeleteCertificateRequest["error"],
      DeleteCertificateRequest["request"]
    >
    client?: DeleteCertificateRequest["client"]["parameters"]
  } = {}
): UseMutationResult<
  DeleteCertificateRequest["response"],
  DeleteCertificateRequest["error"],
  DeleteCertificateRequest["request"]
> {
  const { mutation: mutationOptions, client: clientOptions = {} } =
    options ?? {}
  return useMutation<
    DeleteCertificateRequest["response"],
    DeleteCertificateRequest["error"],
    DeleteCertificateRequest["request"]
  >({
    mutationFn: async (data) => {
      const res = await client<
        DeleteCertificateRequest["data"],
        DeleteCertificateRequest["error"],
        DeleteCertificateRequest["request"]
      >({
        method: "delete",
        url: `/v1/certificate-request/delete-certificate-request`,
        data,
        ...clientOptions,
      })
      return res
    },
    ...mutationOptions,
  })
}
